import '@radix-ui/themes/styles.css';
import { Flex, Box, Card, Button, ScrollArea, AlertDialog } from '@radix-ui/themes';
import { FilterCard } from './FilterCard'; // Ensure the path is correct
import { motion } from 'framer-motion';

/**
 * FilterCol component that renders a column of filter cards for countries, ISPs, and regions/cities.
 * 
 * @param {Object} props - The component props.
 * @param {boolean} props.isPhone - Boolean to indicate if the device is a phone.
 * @param {Array} props.countryFilters - List of country filter objects, each representing a country's selected ISPs and regions.
 * @param {Function} props.onCountryLockChange - Callback function to handle toggling the lock state of a country filter card.
 * @param {Function} props.filter_change_callback - Callback function to handle filter changes (ISP and city/region selections).
 * @param {Function} props.purgeCards - Callback function to clear all country filter cards.
 * @param {Function} props.onCountryDeleteCallback - Callback function to delete a specific country filter card.
 * @param {Function} props.onCountryCopyCallback - Callback function to copy a specific country filter card.
 * @returns {JSX.Element} The rendered filter column component.
 */
export function FilterCol({ isPhone, countryFilters, onCountryLockChange, filter_change_callback, purgeCards, onCountryDeleteCallback, onCountryCopyCallback }) {
  const screenWidth = window.innerWidth;
  const card_style = { minWidth: '23vw', height: "90vh", position: "fixed", padding: '25px', borderRight: '1px solid #ccc', right: "0", marginTop: "4rem" }
  if (screenWidth >= 1000 && screenWidth <= 1440) {
    card_style.minWidth = '30vw'
  }

  /**
 * Handles ISP selection for a specific country and city.
 * 
 * @param {string} countryName - The name of the country.
 * @param {string} isp - The selected ISP.
 * @param {number} id - The index of the filter card.
 * @param {string} city - The selected city or region.
 */
  function onIspSelect(countryName, isp, id, city) {
    filter_change_callback(countryName, isp, id, city)
  }

  /**
   * Clears all country filter cards by calling the purgeCards callback.
   */
  const onPurgeCards = () => {
    purgeCards();
  };

  /**
   * Toggles the lock state of a specific country filter card.
   * 
   * @param {number} index - The index of the country filter card.
   */
  const toggleCardLock = (index) => {
    onCountryLockChange(index, !countryFilters[index].locked)
    console.log(countryFilters)
  };

  /**
   * Deletes a specific country filter card.
   * 
   * @param {number} index - The index of the country filter card to delete.
   */
  function onDelete(index) {
    onCountryDeleteCallback(index)
  }

  /**
   * Copies a specific country filter card.
   * 
   * @param {number} index - The index of the country filter card to copy.
   */
  function onCopy(index) {
    onCountryCopyCallback(index)
  }

  return (
    <motion.div initial={{ x: '120vw', opacity: 0 }}  // Start from offscreen left
      animate={{ x: '100vw', opacity: 1 }}     // Animate to visible position
      exit={{ x: -300, opacity: 0 }}      // Animate out back to the left
      transition={{ duration: 0.5 }}>
      <Card size={2} variant='surface' content='center' style={card_style} >
        <Flex gap="5" align="center" direction="column" >
          <Box maxWidth="400px">

            <h1 style={{ textAlign: 'center', fontSize: "30px" }} >
              Countries
            </h1>
          </Box>
          <ScrollArea type="hover" scrollbars="vertical" style={{ height: "75vh" }}>
            <Flex gap="5" align="center" direction="column" >
              {countryFilters.map((country, index) => (
                <FilterCard
                  screenwidth={card_style.minWidth}
                  key={index}
                  card_index={index}
                  initialRegion={country.city}
                  initialISP={country.isp}
                  CountryName={`${country.countryName}`}
                  isLocked={country.locked}
                  onToggleLock={() => toggleCardLock(index)}
                  onIspSelect={onIspSelect}
                  onDelete={() => onDelete(index)}
                  onCopy={() => onCopy(index)}
                />
              ))}

              <AlertDialog.Root>
                <AlertDialog.Trigger>
                  <Button variant="classic" color='red' >Delete Unlocked Cards</Button>
                </AlertDialog.Trigger>
                <AlertDialog.Content maxWidth="450px">
                  <AlertDialog.Title>Delete Unlocked Cards</AlertDialog.Title>
                  <AlertDialog.Description size="2">
                    Are you sure? This will delete all country cards not locked with the blue lock icon.
                  </AlertDialog.Description>

                  <Flex gap="3" mt="4" justify="end">
                    <AlertDialog.Cancel>
                      <Button variant="soft" color="gray">
                        Cancel
                      </Button>
                    </AlertDialog.Cancel>
                    <AlertDialog.Action>
                      <Button variant="solid" color="red" onClick={onPurgeCards}>
                        Yes, Delete
                      </Button>
                    </AlertDialog.Action>
                  </Flex>
                </AlertDialog.Content>
              </AlertDialog.Root>

            </Flex>
          </ScrollArea>
        </Flex>
      </Card>
    </motion.div>
  );
}
